/**
 * Единый конфиг цветового кодирования уровней риска.
 *
 * Все компоненты, отображающие risk level / flags / badges,
 * должны импортировать цвета и иконки отсюда.
 */
import { Archive, CircleAlert, CircleCheck, CircleMinus, CircleX, Info, type LucideIcon } from "lucide-react";

/* ─── Типы ─── */

export type RiskGrade = "A" | "B" | "C" | "D";
export type InfoStatus = "FYI" | "ARCHIVED";
export type RiskOrInfoStatus = RiskGrade | InfoStatus;

/** Маппинг бэкенд-значений на grade-литерал */
export type BackendRiskLevel = "A_GREEN" | "B_YELLOW" | "C_ORANGE" | "D_RED";

export interface RiskLevelStyle {
  /** Основной цвет текста/иконок */
  color: string;
  /** Фоновый цвет */
  bg: string;
  /** Lucide иконка */
  icon: LucideIcon;
  /** Русское название уровня */
  label: string;
}

/* ─── Конфиг ───
 *
 * Цвета — ссылки на CSS-переменные темы (--c-risk-*, см. index.css), а не
 * hex: в тёмной и светлой теме уровни риска подобраны отдельно. Раньше тут
 * стояли светлые Material-hex, и бледные фоны вроде #FFEBEE горели пятнами
 * на тёмном экране. Прозрачность добавляйте через withAlpha(), а не
 * приклеиванием hex-суффикса.
 */

type RiskToken = "a" | "b" | "c" | "d" | "fyi" | "archived";

function riskVar(token: RiskToken, alpha?: number): string {
  return alpha === undefined ? `rgb(var(--c-risk-${token}))` : `rgb(var(--c-risk-${token}) / ${alpha})`;
}

/**
 * Цвет с прозрачностью: понимает и rgb(var(--…)), и hex.
 * withAlpha(RISK_STYLES.D.color, 0.05) → "rgb(var(--c-risk-d) / 0.05)".
 */
export function withAlpha(color: string, alpha: number): string {
  if (color.startsWith("rgb(var(") && color.endsWith("))")) {
    return `${color.slice(0, -1)} / ${alpha})`;
  }
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    const suffix = Math.round(alpha * 255)
      .toString(16)
      .padStart(2, "0");
    return `${color}${suffix}`;
  }
  return color;
}

export const RISK_STYLES: Record<RiskOrInfoStatus, RiskLevelStyle> = {
  ARCHIVED: {
    color: riskVar("archived"),
    bg: riskVar("archived", 0.12),
    icon: Archive,
    label: "Архив",
  },
  FYI: {
    color: riskVar("fyi"),
    bg: riskVar("fyi", 0.12),
    icon: Info,
    label: "Информация",
  },
  A: {
    color: riskVar("a"),
    bg: riskVar("a", 0.12),
    icon: CircleCheck,
    label: "Низкий риск",
  },
  B: {
    color: riskVar("b"),
    bg: riskVar("b", 0.12),
    icon: CircleMinus,
    label: "Средний риск",
  },
  C: {
    color: riskVar("c"),
    bg: riskVar("c", 0.12),
    icon: CircleAlert,
    label: "Высокий риск",
  },
  D: {
    color: riskVar("d"),
    bg: riskVar("d", 0.12),
    icon: CircleX,
    label: "Критический риск",
  },
};

/* ─── Размеры иконок (единый стандарт фронта и PDF) ───
 *
 * inline (16px / 10pt) — иконки в строке текста, бейджи статуса (Подтвержден,
 *   Не проверен и т.п.), suffix-иконки в полях (cake возле возраста), маркеры
 *   в списках факторов риска.
 * section (20px / 12pt) — иконка-заголовок секционной карточки (Shield в
 *   риск-профиле, иконка скоринга в header'е).
 *
 * На фронте используйте Tailwind-классы (`w-4 h-4` / `w-5 h-5`); в PDF —
 * параметр `size` макроса (`"10pt"` / `"12pt"`).
 */
export const ICON_SIZE = {
  /** Tailwind class для inline-иконок: 16px. */
  inline: "w-4 h-4",
  /** Tailwind class для иконок в section-header: 20px. */
  section: "w-5 h-5",
} as const;

/* ─── Хелперы ─── */

const BACKEND_TO_GRADE: Record<BackendRiskLevel, RiskGrade> = {
  A_GREEN: "A",
  B_YELLOW: "B",
  C_ORANGE: "C",
  D_RED: "D",
};

/** Получить grade из бэкенд-значения ("A_GREEN" → "A") */
export function gradeFromBackend(level: string): RiskGrade {
  return BACKEND_TO_GRADE[level as BackendRiskLevel] ?? "A";
}

/** Получить стиль из бэкенд-значения ("A_GREEN" → RISK_STYLES.A) */
export function riskStyleFromBackend(level: string): RiskLevelStyle {
  return RISK_STYLES[gradeFromBackend(level)];
}

/** Получить стиль по grade ("A" → RISK_STYLES.A) */
export function riskStyleByGrade(grade: string): RiskLevelStyle {
  return RISK_STYLES[(grade?.[0]?.toUpperCase() ?? "A") as RiskGrade] ?? RISK_STYLES.A;
}

/** Получить только цвет по бэкенд-значению */
export function riskColor(level: string): string {
  return riskStyleFromBackend(level).color;
}

/** Получить только фон по бэкенд-значению */
export function riskBg(level: string): string {
  return riskStyleFromBackend(level).bg;
}
